// src/pages/HomePage.jsx

import React, { useRef, useState, useEffect, useContext } from "react";
import { AuthContext } from "../user/AuthContext";
import { Link } from "react-router-dom";
import FullscreenSketchfab from "../LexUniverse";
import Tilt from "react-parallax-tilt";
import { motion, AnimatePresence } from "framer-motion";
import { FaWindowMinimize, FaWindowRestore, FaCubes } from "react-icons/fa";
import BuilderPage from "../people/BuilderPage";
import HomepageOverlayTemplate, { panelConfigs } from "./HomepageOverlayTemplate";
import "./HomePage.css";
import MinimizedBuilder from "../people/MinimizedBuilder";
import MiniGLBViewer from "./Spaceship";
import ProductsPanel from "./Products";

export default function HomePage() {
  const videoRef = useRef(null);

  const [showHomePanel, setShowHomePanel] = useState(true);
  const [minimizedHome, setMinimizedHome] = useState(false);
  const [showBuilderPanel, setShowBuilderPanel] = useState(true);
  const [minimizedBuilder, setMinimizedBuilder] = useState(true);
  const [showCalendly, setShowCalendly] = useState(false);
  const { user } = useContext(AuthContext);

  // video auto–ping–pong
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let dir = 1, id = null;
    const onLoaded = () => {
      v.currentTime = 0;
      const fps = 30;
      id = setInterval(() => {
        let next = v.currentTime + dir * (1 / fps);
        if (next >= v.duration) { next = v.duration; dir = -1; }
        else if (next <= 0) { next = 0; dir = 1; }
        v.currentTime = next;
      }, 1000 / fps);
    };
    v.preload = "auto";
    v.muted = true;
    v.playsInline = true;
    v.pause();
    v.addEventListener("loadedmetadata", onLoaded);
    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      clearInterval(id);
    };
  }, []);

  const homeVariants = {
    open:    { top: "5vh", left: "7vw", width: "80vw", height: "80vh", borderRadius: 16 },
    minimized: { top: "10vh", left: "10vw", width: 200, height: 120, borderRadius: 12 },
  };
  const builderVariants = {
    open:    { top: "5vh", left: "7vw", width: "80vw", height: "80vh", borderRadius: 16 },
    minimized: { top: "10vh", left: "calc(10vw + 210px)", width: 200, height: 120, borderRadius: 12 },
  };

  return (
    <>
      {/* full‐screen 3D world */}
      <div className="sketchfab-fullscreen">
        <FullscreenSketchfab />
      </div>

      {/* Home Panel */}
      <AnimatePresence>
        {showHomePanel && (
          <Tilt
            tiltMaxAngleX={8}
            tiltMaxAngleY={8}
            glareEnable
            glareMaxOpacity={0.1}
            style={{ position: "absolute", width: "100%", height: "100%", pointerEvents: "none" }}
          >
            <motion.div
              className={`homepage-overlay${minimizedHome ? " minimized" : ""}`}
              initial={minimizedHome ? "minimized" : "open"}
              animate={minimizedHome ? "minimized" : "open"}
              exit={{ opacity: 0 }}
              variants={homeVariants}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={() => setMinimizedHome(false)}
              style={{
                position: "fixed",
                zIndex: 10,
                width: minimizedHome ? 200 : undefined,
                height: minimizedHome ? 120 : undefined,
                top: minimizedHome ? "10vh" : undefined,
                left: minimizedHome ? "10vw" : undefined,
                borderRadius: minimizedHome ? 12 : undefined,
                background: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(12px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                overflow: "hidden",
                pointerEvents: "auto",
                cursor: "pointer",
              }}
            >
              <div className="overlay-toolbar">
                <button
                  onClick={e => { e.stopPropagation(); setMinimizedHome(v => !v); }}
                  title={minimizedHome ? "Restore" : "Minimize"}
                  style={{ cursor: "pointer" }}
                >
                  {minimizedHome ? <FaWindowRestore /> : <FaWindowMinimize />}
                </button>
              </div>
              {!minimizedHome && (
                <div className="homepage-content">
                  <section className="start-row">
                    <div className="video-background">
                      <video ref={videoRef} src="/images/aivid.mp4" preload="auto" muted playsInline />
                    </div>
                    <div className="start-container">
                      <div className="start-content">
                        <h1 className="start-headline">Home of AI Agents</h1>
                        <p className="start-subheadline">
                          Now anyone can build, run, partner, sell their AI agents<br/>
                          — Our goal is to ensure your success 🤖
                        </p>
                        <div className="start-interactive">
                          <button 
                            className="cta-button"
                            onClick={() => setShowCalendly(true)}
                          >
                            Book a Demo
                          </button>
                          <span className="start-separator">Or</span>
                          <button className="apply-button">
                            Scroll Down 
                          </button>
                        </div>
                        {showCalendly && (
                          <div className="calendly-dropdown">
                            <button
                              className="close-dropdown-button"
                              onClick={() => setShowCalendly(false)}
                            >
                              Close
                            </button>
                            <iframe
                              className="calendly-embed"
                              src="https://calendly.com/het081-g/30min?embed_type=Inline"
                              frameBorder="0"
                              title="Lex Smart Connect"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="products-wrapper">
                    <ProductsPanel />
                  </section>

                  <div className="banners-container">
                    <Link to="/gallery" className="banner-link" target="_blank" rel="noopener">
                      <section className="banner-row">
                        <img src="/images/banner1.jpg" alt="Banner1"/>
                      </section>
                    </Link>
                    <section className="banner-row">
                      <img src="/images/banner12.png" alt="Banner12"/>
                    </section>
                  </div>

                  <footer className="homepage-footer">
                    <div className="footer-left">
                      &copy; {new Date().getFullYear()} LEXTRACK AI. ALL RIGHTS RESERVED.
                      <br />
                      Phone: +1 571-352-0503
                      <br />
                      Email: ceo@lextrackai.com
                    </div>
                    <div className="footer-right">
                      <a href="https://lextrackai.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                      <span style={{ margin: "0 8px" }}>|</span>
                      <a href="https://lextrackai.com/terms" target="_blank" rel="noopener noreferrer">Terms</a>
                      <span style={{ margin: "0 8px" }}>|</span>
                      <a href="https://lextrackai.com/opt-in" target="_blank" rel="noopener noreferrer">Opt-In</a>
                    </div>
                  </footer>
                </div>
              )}
            </motion.div>
          </Tilt>
        )}
      </AnimatePresence>

      {/* Builder Panel */}
      <AnimatePresence>
        {showBuilderPanel && minimizedHome && (
          <Tilt
            tiltMaxAngleX={8}
            tiltMaxAngleY={8}
            glareEnable
            glareMaxOpacity={0.1}
            style={{ position: "absolute", width: "100%", height: "100%", pointerEvents: "none" }}
          >
            <motion.div
              className={`builder-overlay${minimizedBuilder ? " minimized" : ""}`}
              initial={minimizedBuilder ? "minimized" : "open"}
              animate={minimizedBuilder ? "minimized" : "open"}
              exit={{ opacity: 0 }}
              variants={builderVariants}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={() => setMinimizedBuilder(false)}
              style={{
                position: "fixed",
                zIndex: 8,
                background: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(12px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                overflow: "hidden",
                pointerEvents: "auto",
                cursor: "pointer",
              }}
            >
              <div className="overlay-toolbar">
                <button
                  onClick={e => { e.stopPropagation(); setMinimizedBuilder(v => !v); }}
                  title={minimizedBuilder ? "Restore" : "Minimize"}
                  style={{ cursor: "pointer" }}
                >
                  {minimizedBuilder ? <FaCubes /> : <FaWindowMinimize />}
                </button>
              </div>
              {minimizedBuilder ? (
                <MinimizedBuilder
                  base={user?.builderConfig?.base ?? "Body_010"}
                  accessories={user?.builderConfig?.accessories ?? []}
                />
              ) : (
                <div style={{ width: "100%", height: "calc(100% - 40px)", overflow: "hidden" }}>
                  <BuilderPage />
                </div>
              )}
            </motion.div>
          </Tilt>
        )}
      </AnimatePresence>

      <MiniGLBViewer url="/models/spaceship.glb" />
    </>
  );
}
